import React from "react";
import { makeStyles } from "tss-react/mui";

import { Box, Button, Snackbar, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import useAppVersionCheck from "../../hooks/useAppVersionCheck";

const useStyles = makeStyles()((theme) => ({
  root: {
    minWidth: 320,
    maxWidth: 420,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    boxShadow: theme.shadows[8],
    padding: theme.spacing(2, 2.25),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 3,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  description: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    lineHeight: 1.45,
  },
  versionLine: {
    fontSize: 11,
    color: theme.palette.text.disabled,
    fontVariantNumeric: "tabular-nums",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  later: {
    textTransform: "none",
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  primary: {
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  },
}));

const AppUpdateNotifier = () => {
  const { classes } = useStyles();
  const {
    updateAvailable,
    currentVersion,
    latestVersion,
    performUpdate,
  } = useAppVersionCheck();

  const [dismissed, setDismissed] = React.useState(false);

  const open = updateAvailable && !dismissed;

  const handleLater = () => setDismissed(true);

  const handleUpdate = () => {
    setDismissed(true);
    performUpdate();
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      sx={{ zIndex: (t) => t.zIndex.snackbar + 10 }}
    >
      <Box className={classes.root} role="alert" aria-live="polite">
        <Box className={classes.header}>
          <span className={classes.badge}>Atualização</span>
          <Typography className={classes.title}>
            Nova versão disponível
          </Typography>
        </Box>

        <Typography className={classes.description}>
          Recarregue para aplicar a versão mais recente do sistema. Suas
          conversas e estado serão preservados após o reload.
        </Typography>

        {(currentVersion || latestVersion) && (
          <Typography className={classes.versionLine}>
            {currentVersion ? `Atual: ${currentVersion}` : ""}
            {currentVersion && latestVersion ? "  •  " : ""}
            {latestVersion ? `Disponível: ${latestVersion}` : ""}
          </Typography>
        )}

        <Box className={classes.actions}>
          <Button
            size="small"
            className={classes.later}
            onClick={handleLater}
          >
            Mais tarde
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon fontSize="small" />}
            className={classes.primary}
            onClick={handleUpdate}
          >
            Atualizar agora
          </Button>
        </Box>
      </Box>
    </Snackbar>
  );
};

export default AppUpdateNotifier;
